import { useState } from 'react';
import type { VenueDto } from '@/services/platform/api/model';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Icon } from '@/components/ui/icon';
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupLabel,
  RadioGroupSupportText,
} from '@/components/ui/radio-group';
import { useClientTranslation } from '@/lib/core/i18n';
import { cn } from '@/lib/core/utils/cn';
import type { StayWhereSheetProps } from '../types';
import { VenueClosedSheet } from '../../shared/sheets/venueClosed';

export function StayWhereSheet({
  isOpen,
  onClose,
  cmsProps,
  venues,
  selectedVenueKey,
  isMember,
  onSelect,
}: StayWhereSheetProps) {
  const t = useClientTranslation();

  const title = cmsProps?.title ?? t('App.Labels.Where');
  const subtitle = cmsProps?.subtitle;

  const [closedVenue, setClosedVenue] = useState<VenueDto | null>(null);

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="border-jncquoi-neutral-200 h-[80dvh] min-h-[min(650px,100dvh)] border-t-[0.5px]">
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-2 px-4 pt-8">
          <div className="flex items-center gap-8">
            <h2 className="font-pp-neue-montreal flex-1 text-[32px] font-medium tracking-[0.5px] text-[#2a2a2a]">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label={t('App.Labels.Close')}
            >
              <Icon
                name="xmark"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-1000"
              />
            </button>
          </div>
          {subtitle && (
            <p className="font-pp-neue-montreal text-[16px] tracking-[0.25px] text-[#707070]">{subtitle}</p>
          )}
        </div>

        {/* Scrollable list */}
        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
          <div className="px-4 pt-6 pb-8">
            <RadioGroup
              value={selectedVenueKey ?? ''}
              onValueChange={key => {
                const venue = venues.find(v => v.venueNodeKey === key);
                if (venue) {
                  onSelect(venue);
                  onClose();
                }
              }}
              className="flex flex-col"
            >
              {venues.map(venue => {
                const memberLocked = (venue.membersOnly ?? false) && !isMember;
                const closed = !memberLocked && (venue.isVenueClosed ?? false);
                const disabled = memberLocked || closed;
                const itemId = `where-${venue.venueNodeKey}`;

                return (
                  <RadioGroupLabel
                    key={venue.venueNodeKey}
                    htmlFor={itemId}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 border-b-[0.5px] border-[#e1e1e1] py-[24px] text-[20px] tracking-normal text-[#1a1a1a]',
                      disabled && 'opacity-40',
                      memberLocked && 'pointer-events-none'
                    )}
                    // Closed venues stay tappable so they can explain why and offer alternatives.
                    onClick={event => {
                      if (!closed) return;
                      event.preventDefault();
                      setClosedVenue(venue);
                    }}
                  >
                    {/* A disabled radio swallows its own taps, so let them fall through to the label. */}
                    <RadioGroupItem
                      id={itemId}
                      value={venue.venueNodeKey ?? ''}
                      disabled={disabled}
                      className={cn(closed && 'pointer-events-none')}
                    />

                    <div className="flex flex-col items-start gap-1">
                      <span className="font-pp-neue-montreal font-normal">{venue.name}</span>
                      {memberLocked && (
                        <RadioGroupSupportText className="rounded-[2px] bg-[#f4f4f4] px-0 py-0.5 text-[11px] font-medium tracking-[0.5px] text-[#9a9a9a] uppercase">
                          {t('App.Labels.MembersOnly')}
                        </RadioGroupSupportText>
                      )}
                    </div>
                  </RadioGroupLabel>
                );
              })}
            </RadioGroup>
          </div>
        </div>

        <VenueClosedSheet venue={closedVenue} onClose={() => setClosedVenue(null)} />
      </DrawerContent>
    </Drawer>
  );
}
